/**
 * Reusable hook for async data fetching with automatic loading state management.
 *
 * Provides a standardized way to handle async data fetching, loading states,
 * and error handling. Eliminates duplicate loading state patterns across hooks.
 * Includes request deduplication to prevent duplicate API calls when multiple
 * components use the same hook with identical parameters.
 *
 * @module useAsyncData
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Global map to track ongoing requests for deduplication.
 * Key: string representation of fetchFn + dependencies
 * Value: Promise that resolves to the fetched data
 */
const ongoingRequests = new Map<string, Promise<unknown>>();

/**
 * Generate a unique key for request deduplication based on caller-supplied
 * dependencies. The function body is included as a tiebreaker for hooks that
 * share the same deps shape but fetch different endpoints.
 */
function generateRequestKey(fetchFn: () => Promise<unknown>, dependencies: React.DependencyList): string {
  try {
    const fnString = fetchFn.toString();
    const depsString = JSON.stringify(dependencies);
    return `${fnString}|${depsString}`;
  } catch {
    return `fallback-${Date.now()}-${Math.random()}`;
  }
}

export interface UseAsyncDataReturn<T> {
  /** The fetched data, or null if not yet loaded or on error */
  readonly data: T | null;
  /** Whether data is currently being fetched */
  readonly isLoading: boolean;
  /** Error object if fetch failed, or null */
  readonly error: Error | null;
  /** Function to manually refetch the data */
  readonly refetch: () => Promise<void>;
}

/**
 * Hook for managing async data fetching with automatic loading state
 *
 * Automatically fetches data on mount and when dependencies change.
 * Manages loading state, error state, and provides a refetch function.
 *
 * @param fetchFn - Async function that returns the data to fetch
 * @param dependencies - Array of dependencies that trigger refetch when changed (like useEffect)
 * @returns Object containing data, loading state, error, and refetch function
 *
 * @example
 * ```typescript
 * const { data, isLoading, error, refetch } = useAsyncData(
 *   async () => {
 *     const response = await fetch('/api/data');
 *     return response.json();
 *   },
 *   [userId] // Refetch when userId changes
 * );
 * ```
 */
export function useAsyncData<T>(
  fetchFn: () => Promise<T>,
  dependencies: React.DependencyList = [],
): UseAsyncDataReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);

  // Keep a stable ref to the latest fetchFn so the effect deps don't change
  // every render when callers pass inline arrow functions.
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  // Track if component is mounted to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    // Don't show loading spinner if a fetch is already in flight for this key
    const requestKey = generateRequestKey(fetchFnRef.current, dependencies);
    if (!ongoingRequests.has(requestKey)) {
      setIsLoading(true);
    }
    setError(null);

    // Check if a request with the same key is already in progress
    let requestPromise = ongoingRequests.get(requestKey) as Promise<T> | undefined;

    if (requestPromise === undefined) {
      // Create a new request promise
      requestPromise = (async () => {
        try {
          const result = await fetchFnRef.current();
          return result;
        } finally {
          // Clean up the request from the map when it completes (success or error)
          ongoingRequests.delete(requestKey);
        }
      })();

      // Store the promise in the map for deduplication
      ongoingRequests.set(requestKey, requestPromise);
    }

    try {
      // Wait for the promise (either new or existing)
      const result = await requestPromise;
      if (isMountedRef.current) {
        setData(result);
        setIsLoading(false);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setIsLoading(false);
        // Keep previous data on error (don't set to null)
      }
    }
  // dependencies is the caller-supplied list that should actually trigger refetches;
  // fetchFnRef is a ref so it never needs to be in the deps array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // Fetch data on mount and when caller dependencies change
  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Refetch function for manual refresh
  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}

